import { z } from 'zod';

export const WebFetchInputSchema = z.object({ url: z.string() });
export type WebFetchInput = z.infer<typeof WebFetchInputSchema>;

export const WEB_FETCH_ERROR_CODES = [
  'invalid_input',
  'url_too_long',
  'url_not_allowed',
  'url_not_in_prior_context',
  'url_not_accessible',
  'too_many_requests',
  'unsupported_content_type',
  'max_uses_exceeded',
  'unavailable',
] as const;
export type WebFetchErrorCode = (typeof WEB_FETCH_ERROR_CODES)[number];

export type WebPageKind = 'html' | 'text' | 'json' | 'csv' | 'pdf';

export interface WebFetchToolError {
  readonly type: 'web_fetch_tool_result_error';
  readonly errorCode: WebFetchErrorCode;
  readonly message: string;
}

// Anthropic's web_fetch result, as its AI SDK provider stores it, plus what Plato adds.
export interface WebFetchOutput {
  readonly type: 'web_fetch_result';
  readonly url: string;
  readonly retrievedAt: string;
  readonly content: {
    readonly type: 'document';
    readonly title: string | null;
    readonly source:
      | { readonly type: 'text'; readonly mediaType: 'text/plain'; readonly data: string }
      | { readonly type: 'base64'; readonly mediaType: 'application/pdf'; readonly data: string };
  };
  readonly plato: WebFetchDetails;
}

export interface WebFetchDetails {
  readonly requested_url: string;
  readonly kind: WebPageKind;
  readonly bytes: number;
  readonly pages: number | null;
  readonly tokens: number;
  // full: the text in full; document: the PDF itself; preview: the start of the text.
  readonly shown: 'full' | 'document' | 'preview';
  readonly text?: string;
  readonly saved_to: readonly string[];
  readonly links: readonly string[];
}

export interface WebSearchResult {
  readonly url: string;
  readonly title: string | null;
  readonly pageAge: string | null;
}

export interface WebFetchResult {
  readonly url: string;
  readonly title: string | null;
  readonly retrievedAt: string | null;
  readonly mediaType: string | null;
}

export function readWebSearchResults(output: unknown): WebSearchResult[] | null {
  if (!Array.isArray(output)) return null;
  const results: WebSearchResult[] = [];
  for (const item of output) {
    if (!isRecord(item) || item.type !== 'web_search_result' || typeof item.url !== 'string') {
      return null;
    }
    results.push({
      url: item.url,
      title: typeof item.title === 'string' ? item.title : null,
      pageAge: typeof item.pageAge === 'string' ? item.pageAge : null,
    });
  }
  return results;
}

export function readWebFetchResult(output: unknown): WebFetchResult | null {
  if (!isRecord(output) || output.type !== 'web_fetch_result' || typeof output.url !== 'string') {
    return null;
  }
  const content = isRecord(output.content) ? output.content : {};
  const source = isRecord(content.source) ? content.source : {};
  const pdf = isRecord(output.plato) && output.plato.kind === 'pdf';
  return {
    url: output.url,
    title: typeof content.title === 'string' ? content.title : null,
    retrievedAt: typeof output.retrievedAt === 'string' ? output.retrievedAt : null,
    mediaType: pdf
      ? 'application/pdf'
      : typeof source.mediaType === 'string'
        ? source.mediaType
        : null,
  };
}

export function webErrorCode(value: unknown): string | null {
  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!isRecord(parsed) || typeof parsed.type !== 'string') return null;
  if (!parsed.type.endsWith('_tool_result_error')) return null;
  return typeof parsed.errorCode === 'string' ? parsed.errorCode : 'unknown';
}

export function webOutputForBrowser(output: unknown): unknown {
  if (readWebSearchResults(output) !== null) {
    return (output as Record<string, unknown>[]).map(({ encryptedContent: _, ...rest }) => rest);
  }
  if (readWebFetchResult(output) !== null) {
    const result = output as Record<string, unknown>;
    const content = result.content as Record<string, unknown>;
    const source = (isRecord(content.source) ? content.source : {}) as Record<string, unknown>;
    const stripped = { ...result, content: { ...content, source: { ...source, data: '' } } };
    if (!isRecord(result.plato)) return stripped;
    const { text: _text, links: _links, ...plato } = result.plato;
    return { ...stripped, plato };
  }
  return output;
}

export function isReplayableWebSearch(output: unknown): boolean {
  if (readWebSearchResults(output) === null) return false;
  return (output as Record<string, unknown>[]).every(
    (item) => typeof item.encryptedContent === 'string' && item.encryptedContent !== '',
  );
}

export function webDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
